import { useCallback } from "react";
import { CartProduct, Product } from "@/types/product";
import { UnifiedMessage } from "@/hooks/use-messages";
import { ProductDetails } from "./ProductDetails";
import ChatMessages from "./ChatMessages";
import { getProductVariantData } from "@/utils/get-product-variant-data";
import { ReconnectPrompt } from "./ReconnectPrompt";
import { ChatFooterText } from "./ChatFooterText";
import { useCompatibleVoiceAssistant } from "@/hooks/use-compatible-voice-assistant";

type TextModeProps = {
  messages: UnifiedMessage[];
  selectedProduct: Product | null;
  onSelectProduct: (product: Product | null) => void;
  cartItems: CartProduct[];
  onAddToCart: (product: Product | null) => void;
  triggerScrollToBottomCounter: number;
  showReconnectPrompt: boolean;
  sendChat: (message: string) => void;
  handleBasketClick: () => void;
};

export default function TextMode({
  messages,
  selectedProduct,
  onSelectProduct,
  cartItems,
  onAddToCart,
  triggerScrollToBottomCounter,
  showReconnectPrompt,
  sendChat,
  handleBasketClick,
}: TextModeProps) {
  const agent = useCompatibleVoiceAssistant();
  const handleNoClick = useCallback(() => {
    onSelectProduct(null);
  }, [onSelectProduct]);
  const handleYesClick = useCallback(() => {
    onAddToCart(selectedProduct);
  }, [onAddToCart, selectedProduct?.id]);
  const { variantToShow, image, formattedPrice } =
    getProductVariantData(selectedProduct);

  return (
    <>
      {selectedProduct && (
        <>
          <div className="widget-displaying-results nut-w-[100%]">
            <ProductDetails
              image={image}
              title={selectedProduct.title}
              currentPrice={formattedPrice || "N/A"}
              onYesClick={handleYesClick}
              onNoClick={handleNoClick}
              onCheckoutClick={handleBasketClick}
              productUrl={
                selectedProduct.product_url ||
                (selectedProduct.store_url && selectedProduct.handle
                  ? `${selectedProduct.store_url.replace(/\/$/, "")}/products/${selectedProduct.handle}`
                  : undefined)
              }
              isAddedToCart={cartItems.some(
                (item) => item.variant_id === variantToShow?.id,
              )}
            />
          </div>
        </>
      )}
      <ChatMessages
        triggerScrollToBottomCounter={triggerScrollToBottomCounter}
        messages={messages}
        onSelectProduct={onSelectProduct}
        agentState={agent.state}
      />
      {showReconnectPrompt && <ReconnectPrompt />}
      <ChatFooterText
        cartItems={cartItems.length}
        agentState={agent.state}
        sendChat={sendChat}
        onBasketClick={handleBasketClick}
      />
    </>
  );
}
